import { ANALYTICS_CONFIG } from '../../../config/analytics-config.js';
import { LoadingState, SectionHeader } from '../../../shared/components/index.js';
import { useLeadsData } from '../hooks/useLeadsData.jsx';
import HtmlBlock from '../components/HtmlBlock.jsx';
import LeadProbePanel from '../components/LeadProbePanel.jsx';
import StateEntrySourcesPanel from '../components/StateEntrySourcesPanel.jsx';
import {
  buildCommercialHtml,
  buildCriticalByCicloHtml,
  buildEntryChannelsHtml,
  buildGastoRetornoHtml,
  buildCaminoAlianzasHtml,
  buildTagCampaignMapHtml,
  buildOriginsHtml,
  buildPeriodSourcesHtml,
  buildWarningsHtml,
  buildWebKommoHealthHtml
} from '../utils/present-leads-html.js';

/** Resumen del módulo Leads — radiografía por ciclo + cola crítica por ciclo. */
export default function LeadsResumenPage() {
  const {
    loading,
    data,
    attentionExpanded,
    setAttentionExpanded,
    range,
    periodLabel,
    awaitingCustomDates
  } = useLeadsData();

  if (awaitingCustomDates) {
    return (
      <p className="analytics-empty">
        Elegí <strong>Desde</strong> y <strong>Hasta</strong> para cargar el período personalizado.
      </p>
    );
  }

  if (loading && !data) return <LoadingState />;

  return (
    <>
      <HtmlBlock html={buildWarningsHtml((data && data.warnings) || [])} />
      <section className="analytics-section">
        <SectionHeader
          title={'¿Llegan a Kommo? (' + periodLabel + ')'}
          note="Compara Diagnósticos Web (formulario) vs Kommo Lead ID. Si hay sin Kommo, se están perdiendo en el sync — no en Meta."
        />
        <HtmlBlock html={buildWebKommoHealthHtml(data && data.webKommoHealth)} />
      </section>
      <section className="analytics-section">
        <SectionHeader
          title="Probar lead"
          note="Buscá por ID, teléfono, nombre o etiqueta y mirá origen + campaña. Mismas reglas que Gasto→retorno. Sin sync."
        />
        <LeadProbePanel />
      </section>
      <section className="analytics-section">
        <SectionHeader
          title={'Vía de entrada (' + periodLabel + ')'}
          note="Cómo nació el lead en Kommo. Meta Lead Ads va primero; debajo, el resto (Landing, alianzas, etc.). Meta y Landing se desglosan por campaña Ads. Sin filtro Orígenes."
        />
        <HtmlBlock html={buildEntryChannelsHtml(data && data.entryChannels)} />
      </section>
      <StateEntrySourcesPanel
        stateEntrySources={data && data.stateEntrySources}
        periodLabel={periodLabel}
      />
      <section className="analytics-section">
        <SectionHeader
          title="Camino por campaña (gasto → leads → reuniones → cierre)"
          note={
            'Usá el filtro de período arriba: recalcula gasto Meta, leads creados, reuniones y desenlace (minuta / propuesta / reserva). UF reserva = valor propiedad, no comisión GI.'
          }
        />
        <HtmlBlock html={buildGastoRetornoHtml(data && data.gastoRetorno)} />
      </section>
      <section className="analytics-section">
        <SectionHeader
          title="Camino por alianza (sin costo Meta)"
          note={
            'PatagoLand, Euro y otras etiquetas No Meta: leads → clasificación → reuniones → cierre. El costo de alianza no se mide como CPL Meta. Mismo período del filtro arriba. Clasificá etiquetas en Config → Orígenes.'
          }
        />
        <HtmlBlock html={buildCaminoAlianzasHtml(data && data.caminoAlianzas)} />
      </section>
      <section className="analytics-section">
        <SectionHeader
          title="Etiquetas Kommo ↔ campañas Meta"
          note={
            'Qué etiquetas aparecen en los leads matcheados a cada campaña Ads (período arriba). Útil para tags crípticos de Lead Ads (ej. invierte-sin-pie…). Clasificar Meta/No Meta sigue en Config → Orígenes.'
          }
        />
        <HtmlBlock html={buildTagCampaignMapHtml(data && data.tagCampaignMap)} />
      </section>
      <section className="analytics-section">
        <SectionHeader
          title="Kommo vs Meta (totales del período)"
          note="Totales lado a lado. Arriba: camino Meta, alianzas y mapa etiqueta↔campaña."
        />
        <HtmlBlock html={buildPeriodSourcesHtml(data && data.periodSources)} />
      </section>
      <section className="analytics-section">
        <SectionHeader
          title="Estado comercial"
          note="Tus 3 categorías con sus subestados: cuántos hay en cada una y desde hace cuánto. Sin procesar y En proceso son stock en vivo; Terminado son cierres del período."
        />
        <HtmlBlock
          html={buildCommercialHtml(data && data.snapshot, (data && data.kpis) || {})}
        />
      </section>
      <section className="analytics-section">
        <SectionHeader
          title={'Movimiento en el período (' + periodLabel + ')'}
          note="Leads creados en el rango: Meta por campaña Ads; alianzas por etiqueta. Estado actual de cada grupo."
        />
        <HtmlBlock html={buildOriginsHtml(data && data.originsPeriod)} />
      </section>
      <section className="analytics-section">
        <SectionHeader
          title="Orígenes abiertos (foto en vivo)"
          note="Stock abierto ahora. Meta se corta por campaña (lo pagado); alianzas por etiqueta. Tocá una fila para ver estado interno y etapa Kommo."
        />
        <HtmlBlock html={buildOriginsHtml(data && data.origins)} />
      </section>
      <section className="analytics-section analytics-critical" aria-live="polite">
        <SectionHeader
          title="Leads críticos"
          note="Sin procesar demorados (sin gestión más de 1 día) y En proceso estancados (más de 15 días). Terminado no entra en esta cola."
        />
        <HtmlBlock
          html={buildCriticalByCicloHtml(data && data.attention, {
            expanded: attentionExpanded,
            previewSize: ANALYTICS_CONFIG.attentionPreviewSize
          })}
          onClick={(ev) => {
            if (ev.target.closest('[data-attention-toggle]')) {
              setAttentionExpanded((v) => !v);
            }
          }}
        />
      </section>
    </>
  );
}
